import type { SocialMediaTypes } from '@/types/contentstack';
import { analyticsService } from '@utils/analytics';
import { sanitizeAttr, sanitizeCSS, sanitizeUrl } from '@utils/sanitizer';

const ID_PREFIX = 'social-link';

// Social media brand glyphs are not part of the IDS v2 icon catalog, so they
// are served as local monochrome SVGs (white, sized to sit on the colored
// button background). File names match the Contentstack `social_link.name`.
const BASE = import.meta.env.VITE_BASE ?? '/';
const SOCIAL_ICON_BASE = `${BASE}assets/social-midia-icons`;

export function socialIconUrl(type: SocialMediaTypes | string): string {
  return `${SOCIAL_ICON_BASE}/${type}.svg`;
}

export interface SocialLinkParams {
  href: string;
  arialLabel: string;
  type: SocialMediaTypes;
  background: string;
}

export function renderSocialLinks(links: SocialLinkParams[], title: string): string {
  const buttons = links
    .map((link) => {
      const background = sanitizeCSS(link.background);
      const styleAttr = background ? ` style="background: ${background};"` : '';

      return `
        <button
          type="button"
          class="idsw-button idsw-button--main button__social-link"
          aria-label="${sanitizeAttr(link.arialLabel)}"
          id="${ID_PREFIX}-${link.type}-button"
          data-testid="${ID_PREFIX}-${link.type}-button"
          data-href="${sanitizeUrl(link.href)}"
          data-label="${sanitizeAttr(link.arialLabel)}"${styleAttr}
        >
          <img
            src="${sanitizeUrl(socialIconUrl(link.type))}"
            alt=""
            aria-hidden="true"
            data-testid="${ID_PREFIX}-${link.type}-icon"
          />
        </button>`;
    })
    .join('');

  return `
  <div class="social-links__container" data-testid="${ID_PREFIX}-container">
    <div>
      <h2 class="ids-mb-4 ids-mt-6" data-testid="${ID_PREFIX}-social-media-block-title">${sanitizeAttr(title)}</h2>
      <div class="button__container ids-mb-6" data-testid="${ID_PREFIX}social-media-list">
        ${buttons}
      </div>
    </div>
  </div>`;
}

export function attachSocialLinksListeners(
  root: ParentNode,
  title: string,
): void {
  const buttons = root.querySelectorAll<HTMLButtonElement>(
    `[data-testid$="-button"][id^="${ID_PREFIX}-"]`,
  );

  buttons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const href = button.dataset.href ?? '';
      const label = button.dataset.label ?? '';

      analyticsService.event({
        link: {
          category: 'Clique',
          action: title,
          label: `BTN:SocialLink:${title}:${label}`,
        },
      });

      if (href) {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
    });
  });
}
